import { usePackageContext } from '@openx/components-package/utils/context/PackageContext.tsx';
import {
  ALLOWLIST_AQUA_CROSS_APP_NAV,
  ENABLED_AQUA,
  ENABLED_DEAL_SENSITIVE_CATEGORIES,
} from '@openx/constants/feature-flag-keys.ts';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import { Feature_Flags_Enum } from '@openx/types/gqlTypes.ts';
import { CuratedPackageAction } from '@openx/types/package.ts';
import type { I18nTranslate } from '@openx/types/translations.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import {
  isAccountAllowedByAcl,
  isAllowedByCompiledAcl,
  isUserAllowedByAcl,
} from '@openx/utils/permission/validators.ts';
import { type NavNestedRouteConfig, RouterMethod } from '@openx/utils/routes/navTypes.ts';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../components/AuthContext/context.ts';
import { isAdmin, isInternalUser, isMasterOrganisation } from '../../permissions/validators.ts';
import { useFeatureFlag } from '../featureFlags/useFeatureFlag.ts';
import { useHouseholdInsightsAccess } from '../household-insights/hooks/useHouseholdInsightsAccess.ts';
import { BetaChip } from './components/BetaChip.tsx';
import { ExternalRedirectIcon } from './components/ExternalRedirectIcon.tsx';
import { SigviewViews } from './constants.ts';

export const useRoutes = (t: I18nTranslate): NavNestedRouteConfig[] => {
  const { session, currentUser } = useAuth();
  const hasCuratedDealBuyerDiscountFeature = useFeatureFlag(Feature_Flags_Enum.CuratedDealBuyerDiscount);
  const { useIsAllowed, isGrowthbookFeatureOn } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const { usePackagePermissions } = usePackageContext();
  const { hasCuratedPermission } = usePackagePermissions();

  const isDealSensitiveCategoriesPageEnabled = isGrowthbookFeatureOn(ENABLED_DEAL_SENSITIVE_CATEGORIES);
  const hasHouseholdInsightsAccess = useHouseholdInsightsAccess();

  currentUser?.getIdToken();
  const [token, setToken] = useState<string | null>(null);

  const sigview_url = session?.sigview_url;
  const partner_id = session?.account?.exchange_account_partner_id;
  const hasPartnerId = !!partner_id;
  const isMasterOrg = isMasterOrganisation(session);

  const hasInventoryDiscoveryAccess = hasPartnerId && !!sigview_url;

  const hasCuratedPackagesAccess = hasCuratedPermission({
    action: CuratedPackageAction.SHOW_LIST_TAB,
    pckg: { is_curated: true },
  });

  useEffect(() => {
    async function getToken() {
      const token = await currentUser?.getIdToken();
      setToken(token ?? null);
    }

    token ?? getToken();
  }, [currentUser, token]);

  return useMemo(
    () => [
      {
        permissionCheck: () => isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_READ)),
        routes: [
          {
            method: RouterMethod.internal,
            path: '/deals',
            permissionCheck: () => isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_READ)),
            title: t('Deals'),
          },
          {
            method: RouterMethod.internal,
            path: '/deals-sensitive-categories',
            permissionCheck: () => isDealSensitiveCategoriesPageEnabled && isAdmin(session) && isInternalUser(session),
            title: t('Deals Sensitive Categories'),
          },
          {
            method: RouterMethod.internal,
            path: '/discounts',
            permissionCheck: () => hasCuratedDealBuyerDiscountFeature || isMasterOrg,
            title: t('Discounts'),
          },
        ],
        title: t('Activation'),
      },
      {
        permissionCheck: () => isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_READ)) || hasInventoryDiscoveryAccess,
        routes: [
          {
            method: RouterMethod.internal,
            path: '/direct-deals?resetViewType=true',
            permissionCheck: () => true,
            title: t('Direct Deals'),
          },
          {
            activePathOverride: '/inventory/packages',
            method: RouterMethod.internal,
            path: hasCuratedPackagesAccess ? '/inventory/packages/curated' : '/inventory/packages',
            permissionCheck: () => isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_READ)),
            title: t('Packages'),
          },
          {
            Icon: () => <ExternalRedirectIcon />,
            method: RouterMethod.external,
            path: sigview_url ? `${sigview_url}&view=${SigviewViews.INVENTORY_DISCOVERY}` : '',
            permissionCheck: () => hasInventoryDiscoveryAccess,
            title: t('Inventory Discovery'),
          },
          {
            method: RouterMethod.internal,
            path: '/inventory-lists',
            permissionCheck: () => true,
            title: t('Inventory Lists'),
          },
          {
            Icon: BetaChip,
            method: RouterMethod.internal,
            path: '/deal-prompt',
            permissionCheck: () =>
              isAllowed(isUserAllowedByAcl(ACL_TYPE.AI_INVENTORY_DISCOVERY_ENABLED)) ||
              isAllowed(isAccountAllowedByAcl(ACL_TYPE.AI_INVENTORY_DISCOVERY_ENABLED)),
            title: t('Deal Prompt'),
          },
        ],
        title: t('Inventory'),
      },
      {
        permissionCheck: () => isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_READ)),
        routes: [
          {
            method: RouterMethod.internal,
            path: '/audiences',
            permissionCheck: () => true,
            title: t('Audiences'),
          },
          {
            method: RouterMethod.internal,
            path: '/segments',
            permissionCheck: () => true,
            title: t('Segments'),
          },
          {
            method: RouterMethod.internal,
            path: '/taxonomy',
            permissionCheck: () => true,
            title: t('OpenXSelect Taxonomy'),
          },
        ],
        title: t('Data'),
      },
      {
        permissionCheck: () => true,
        routes: [
          {
            method: RouterMethod.internal,
            path: '/deal-insights',
            permissionCheck: () => true,
            title: t('Deal Insights'),
          },
          {
            method: RouterMethod.internal,
            path: '/household-insights',
            permissionCheck: () => hasHouseholdInsightsAccess,
            title: t('Household Insights'),
          },
          {
            Icon: () => <ExternalRedirectIcon />,
            method: RouterMethod.external,
            path: sigview_url ? `${sigview_url}&view=${SigviewViews.CPREF}` : '',
            permissionCheck: () => hasPartnerId && !!sigview_url,
            title: t('Curation Performance'),
          },
        ],
        title: t('Reporting'),
      },
      {
        permissionCheck: () =>
          isGrowthbookFeatureOn(ENABLED_AQUA) && isAllowed(isUserAllowedByAcl(ACL_TYPE.AQUA_ENABLED)),
        routes: [
          {
            Icon: () => <ExternalRedirectIcon />,
            method: RouterMethod.crossApp,
            path: '/direct',
            permissionCheck: () =>
              isGrowthbookFeatureOn(ENABLED_AQUA) && isAllowed(isUserAllowedByAcl(ACL_TYPE.AQUA_ENABLED)),
            title: t('Direct'),
          },
          {
            method: RouterMethod.internal,
            path: '/aqua/creatives',
            permissionCheck: () =>
              isGrowthbookFeatureOn(ENABLED_AQUA) &&
              isGrowthbookFeatureOn(ALLOWLIST_AQUA_CROSS_APP_NAV) &&
              isAllowed(isUserAllowedByAcl(ACL_TYPE.AQUA_ENABLED)),
            title: t('Creatives'),
          },
        ],
        title: t('OpenX Direct'),
      },
      {
        permissionCheck: () => true,
        routes: [
          {
            method: RouterMethod.internal,
            path: '/organizations',
            permissionCheck: () => isAdmin(session),
            title: t('Organizations'),
          },
          {
            method: RouterMethod.internal,
            path: '/providers',
            permissionCheck: () => isAdmin(session),
            title: t('Providers'),
          },
          {
            method: RouterMethod.internal,
            path: '/users',
            permissionCheck: () => isAdmin(session),
            title: t('Users'),
          },
          {
            Icon: () => <ExternalRedirectIcon />,
            method: RouterMethod.external,
            path: `/docs/openxselect/oxs-main/index.html${token ? `?token=${token}` : ''}`,
            permissionCheck: () => true,
            title: t('Documentation'),
          },
        ],
        title: t('Admin & Resources'),
      },
    ],
    [
      hasCuratedDealBuyerDiscountFeature,
      hasCuratedPackagesAccess,
      hasInventoryDiscoveryAccess,
      hasPartnerId,
      isAllowed,
      isGrowthbookFeatureOn,
      isMasterOrg,
      session,
      sigview_url,
      t,
      token,
      isDealSensitiveCategoriesPageEnabled,
      hasHouseholdInsightsAccess,
    ]
  );
};
